import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, inr } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldAlert, Users, ShoppingBag, IndianRupee, MessagesSquare, Lock, Database,
  BadgeCheck, Ban, ShieldCheck, ArrowRight, Eraser,
} from "lucide-react";
import TrafficPanel from "@/components/TrafficPanel";
import LoadTestPanel from "@/components/LoadTestPanel";

const FOUNDER_EMAIL = "rektr.srm@gmail.com";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="label-mono text-slate-400">{label}</span>
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className="mt-3 font-display font-extrabold text-3xl text-ink">{value}</div>
    </div>
  );
}

function Locked({ user }) {
  return (
    <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="admin-locked">
      <div className="mx-auto h-16 w-16 rounded-full bg-crimson/10 grid place-items-center mb-5">
        <Lock size={30} className="text-crimson" />
      </div>
      <h1 className="font-display font-bold text-2xl text-ink">Restricted area</h1>
      <p className="mt-3 text-slate-500">
        The In-Web Data panel is accessible only to the founder account
        (<span className="font-mono text-ink">{FOUNDER_EMAIL}</span>). Access is enforced on the server.
      </p>
      <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left text-sm" data-testid="session-status">
        <div className="label-mono text-slate-400 mb-2">Your current session</div>
        {user ? (
          <>
            <div className="flex justify-between gap-3"><span className="text-slate-500">Signed in as</span><span className="font-mono text-ink truncate">{user.email}</span></div>
            <div className="flex justify-between gap-3 mt-1"><span className="text-slate-500">Role</span><span className="font-mono text-ink">{user.role}</span></div>
            <div className="flex justify-between gap-3 mt-1"><span className="text-slate-500">Founder access</span><span className={user.is_founder ? "text-emerald-600 font-semibold" : "text-crimson font-semibold"}>{user.is_founder ? "granted" : "denied"}</span></div>
          </>
        ) : (
          <div className="text-slate-500">You are not signed in. Log in as the founder to continue.</div>
        )}
      </div>
      <Link to="/"><Button className="mt-6 bg-ink text-white" data-testid="admin-go-home">Back to Home</Button></Link>
    </div>
  );
}

export default function Admin() {
  const { user, ready } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [txns, setTxns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [chats, setChats] = useState([]);
  const [openChat, setOpenChat] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  const isFounder = user && (user.is_founder || (user.email?.toLowerCase() === FOUNDER_EMAIL && user.role === "founder"));

  const loadAll = async () => {
    try {
      const [s, u, t, p, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/transactions"),
        api.get("/admin/payments"), api.get("/admin/chats"),
      ]);
      setStats(s.data); setUsers(u.data); setTxns(t.data); setPayments(p.data); setChats(c.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail, "Could not load admin data")); }
  };

  useEffect(() => { if (isFounder) loadAll(); }, [isFounder]);

  const toggleSuspend = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/suspend`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, suspended: data.suspended } : x)));
      toast.success(data.suspended ? "User suspended" : "User reinstated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const cleanupDemoData = async () => {
    if (!window.confirm("Remove all leftover sample/demo accounts, listings and reviews from this site? This only deletes the original demo data and cannot affect real students.")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup-demo-data");
      const total = (data.users_removed || 0) + (data.listings_removed || 0) + (data.testimonials_removed || 0);
      if (total === 0) {
        toast.success("No demo data found — this site is already clean.");
      } else {
        toast.success(`Removed ${data.listings_removed} demo listings, ${data.testimonials_removed} demo reviews and ${data.users_removed} demo accounts.`);
      }
      await loadAll();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, "Could not remove demo data"));
    } finally {
      setCleaning(false);
    }
  };

  const verifyPayment = async (p) => {
    try {
      const { data } = await api.post(`/admin/payments/${p.id}/verify`);
      setPayments((prev) => prev.map((x) => (x.id === p.id ? { ...x, verified: data.verified } : x)));
      toast.success(data.verified ? "Payment marked verified" : "Marked unverified");
    } catch (e) { toast.error(apiError(e.response?.data?.detail, "Could not update payment")); }
  };

  if (!ready) return null;
  if (!isFounder) return <Locked user={user} />;

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-crimson/10 grid place-items-center"><Database size={20} className="text-crimson" /></div>
          <div>
            <h1 className="font-display font-extrabold text-ink text-2xl lg:text-3xl tracking-tight">In-Web Data</h1>
            <p className="text-sm text-slate-500 flex items-center gap-1.5"><ShieldAlert size={14} className="text-crimson" /> Founder-only view of all platform data</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={cleanupDemoData}
          disabled={cleaning}
          className="border-crimson/30 text-crimson hover:bg-crimson/5"
          data-testid="cleanup-demo-data-btn"
        >
          <Eraser size={15} className="mr-1.5" />
          {cleaning ? "Removing…" : "Remove sample data"}
        </Button>
      </div>

      {stats && (
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Users} label="Total Users" value={stats.total_users} accent="#2563EB" />
          <Stat icon={ShoppingBag} label="Active Listings" value={stats.active_listings} accent="#059669" />
          <Stat icon={IndianRupee} label="Monthly Revenue" value={inr(stats.monthly_revenue)} accent="#D97706" />
          <Stat icon={MessagesSquare} label="Chat Messages" value={stats.messages} accent="#DC2626" />
        </div>
      )}

      <div className="mt-8">
        <Tabs defaultValue="users">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="users" data-testid="admin-tab-users">Profiles</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="admin-tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="chats" data-testid="admin-tab-chats">Chat Logs</TabsTrigger>
            <TabsTrigger value="traffic" data-testid="admin-tab-traffic">Live Traffic</TabsTrigger>
            <TabsTrigger value="loadtest" data-testid="admin-tab-loadtest">Load Test</TabsTrigger>
          </TabsList>

          {/* USERS */}
          <TabsContent value="users">
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="admin-users-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                    <TableHead>Member</TableHead><TableHead>Rating</TableHead><TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-slate-500 font-mono text-xs">{u.email}</TableCell>
                      <TableCell>{u.role === "founder" ? <Badge className="bg-crimson">Founder</Badge> : <Badge variant="secondary">Student</Badge>}</TableCell>
                      <TableCell>{u.is_member ? <BadgeCheck size={16} className="text-emerald-500" /> : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell>{u.rating_avg?.toFixed(1)} <span className="text-slate-400 text-xs">({u.rating_count})</span></TableCell>
                      <TableCell className="text-right">
                        {u.role !== "founder" && (
                          <Button size="sm" variant={u.suspended ? "outline" : "ghost"} onClick={() => toggleSuspend(u)}
                            className={u.suspended ? "text-emerald-600" : "text-crimson"} data-testid={`suspend-btn-${u.id}`}>
                            {u.suspended ? <><ShieldCheck size={14} className="mr-1" /> Reinstate</> : <><Ban size={14} className="mr-1" /> Suspend</>}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* TRANSACTIONS */}
          <TabsContent value="transactions">
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="admin-txn-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Item</TableHead><TableHead>Buyer</TableHead><TableHead>Seller</TableHead>
                  <TableHead>Pickup</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {txns.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No transactions yet</TableCell></TableRow>}
                  {txns.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium">{t.item}</TableCell>
                      <TableCell className="text-slate-500">{t.buyer}</TableCell>
                      <TableCell className="text-slate-500">{t.seller}</TableCell>
                      <TableCell className="text-slate-500 text-xs">{t.pickup || "—"}</TableCell>
                      <TableCell className="font-semibold">{inr(t.price)}</TableCell>
                      <TableCell><Badge className="bg-emerald-500">{t.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* PAYMENTS */}
          <TabsContent value="payments">
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="admin-payments-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Order ID</TableHead><TableHead>Student</TableHead><TableHead>Method</TableHead>
                  <TableHead>Amount</TableHead><TableHead>Reference</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {payments.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No payments yet</TableCell></TableRow>}
                  {payments.map((p) => (
                    <TableRow key={p.order_id}>
                      <TableCell className="font-mono text-xs">{p.order_id}</TableCell>
                      <TableCell>{p.user}<div className="text-xs text-slate-400">{p.email}</div></TableCell>
                      <TableCell>{p.method ? <Badge variant="secondary" className="uppercase">{p.method}</Badge> : "—"}</TableCell>
                      <TableCell className="font-semibold">{inr(p.amount)}</TableCell>
                      <TableCell className="font-mono text-xs">{p.reference || "—"}</TableCell>
                      <TableCell>
                        {p.method === "trial" ? <Badge className="bg-brand">trial</Badge>
                          : p.verified ? <Badge className="bg-emerald-500">verified</Badge>
                          : <Badge className="bg-amber-500">unverified</Badge>}
                      </TableCell>
                      <TableCell>
                        {p.method !== "trial" && (
                          <Button size="sm" variant="outline" onClick={() => verifyPayment(p)} data-testid={`verify-payment-${p.id}`} className="h-8 text-xs">
                            {p.verified ? "Unverify" : "Mark verified"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CHATS */}
          <TabsContent value="chats">
            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" data-testid="admin-chats-list">
                {chats.length === 0 && <div className="text-center text-slate-400 py-8 text-sm">No conversations yet</div>}
                {chats.map((c) => (
                  <button key={c.id} onClick={() => setOpenChat(c)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${openChat?.id === c.id ? "bg-slate-50" : ""}`} data-testid={`admin-chat-${c.id}`}>
                    <div className="font-medium text-ink text-sm truncate">{c.item}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.buyer} ↔ {c.seller} · {c.message_count} msgs</div>
                    {c.pickup && <div className="text-xs text-crimson mt-0.5">📍 {c.pickup}</div>}
                  </button>
                ))}
              </div>
              <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-4 min-h-[300px]">
                {openChat ? (
                  <div className="space-y-2" data-testid="admin-chat-thread">
                    <div className="text-xs text-slate-400 mb-3 label-mono">{openChat.buyer} ↔ {openChat.seller}</div>
                    {openChat.messages.map((m) => (
                      <div key={m.id} className="text-sm">
                        <span className="font-semibold text-ink">{m.sender_name}:</span>{" "}
                        <span className={m.kind === "address" ? "text-crimson" : "text-slate-600"}>{m.text}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid place-items-center h-full text-slate-400 text-sm">Select a conversation to inspect messages</div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="traffic"><TrafficPanel /></TabsContent>
          <TabsContent value="loadtest"><LoadTestPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
